import { z } from 'zod';
import { http } from '@/services/http';

// GET /events/:id/kpis → { event: <view marcianus_v_event_kpis>, checkins, connections }.
// Tolerante: o core pode enriquecer a view; lemos os campos que existem.
const eventKpiSchema = z
  .object({
    id: z.string().optional(),
    title: z.string().optional(),
    max_attendees: z.coerce.number().optional(),
    attendees_count: z.coerce.number().optional(),
    checkin_count: z.coerce.number().optional(),
    price_base: z.coerce.number().optional(),
    pipeline_target: z.coerce.number().nullish(),
    pipeline_actual: z.coerce.number().nullish(),
  })
  .passthrough();

const kpiResponseSchema = z.object({
  event: eventKpiSchema.nullish(),
  checkins: z.coerce.number().optional(),
  connections: z.record(z.string(), z.unknown()).optional(),
  // Receita REAL de bilhetes (SQL: soma de amount_paid de bilhetes válidos) — distinta do pipeline.
  revenue: z.object({ ticket_revenue: z.coerce.number(), tickets_sold: z.coerce.number() }).optional(),
});
export type EventKpi = z.infer<typeof kpiResponseSchema>;

export async function getEventKpis(id: string): Promise<EventKpi> {
  const raw = await http.get<unknown>(`/events/${id}/kpis`);
  return kpiResponseSchema.parse(raw ?? {});
}

// ── Série temporal real (GET /analytics/timeseries) — dados do core, gap-filled ──
export type TSPoint = { t: string; value: number };
export type Metric =
  | 'revenue' | 'marketplace_revenue' | 'tickets' | 'checkins'
  | 'orders' | 'signups' | 'active_users' | 'pageviews';
export type Period = '24h' | '7d' | '30d' | '90d' | '1y';
export type Timeseries = {
  metric: Metric; period: string; kind: 'currency' | 'count'; label: string;
  points: TSPoint[]; source: string;
};

export async function getTimeseries(metric: Metric, period: Period = '30d'): Promise<Timeseries> {
  return http
    .get<Timeseries>(`/analytics/timeseries?metric=${metric}&period=${period}`)
    .catch(() => ({ metric, period, kind: 'count' as const, label: metric, points: [], source: 'unavailable' }));
}

// ── Tracking real (POST /analytics/track) — fire-and-forget, nunca bloqueia UI ──
function sessionId(): string {
  try {
    let s = sessionStorage.getItem('marc:sid');
    if (!s) { s = crypto.randomUUID?.() ?? String(Date.now()); sessionStorage.setItem('marc:sid', s); }
    return s;
  } catch { return 'anon'; }
}

export type TrackOpts = { category?: string; screen?: string; label?: string; value?: number; metadata?: Record<string, unknown> };
export function track(action: string, opts: TrackOpts = {}): void {
  void http.post('/analytics/track', { action, session_id: sessionId(), ...opts }).catch(() => {});
}
